// ============================================
// Environment Configuration
// ============================================
// Centralized access to all environment variables
// with type safety and default values

// ============================================
// Supabase Config
// ============================================

export const SUPABASE_CONFIG = {
  url: import.meta.env.VITE_SUPABASE_URL as string,
  anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
} as const;

// Validate required Supabase config
if (!SUPABASE_CONFIG.url || !SUPABASE_CONFIG.anonKey) {
  console.error(
    'Missing Supabase configuration. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env.local file.'
  );
}

// ============================================
// OneSignal Config
// ============================================

export const ONESIGNAL_CONFIG = {
  appId: import.meta.env.VITE_ONESIGNAL_APP_ID as string | undefined,
  enabled: import.meta.env.VITE_ENABLE_PUSH_NOTIFICATIONS !== 'false',
} as const;

// ============================================
// Feature Flags
// ============================================

export const FEATURES = {
  pushNotifications: 
    ONESIGNAL_CONFIG.enabled && 
    !!ONESIGNAL_CONFIG.appId,
  realTime: import.meta.env.VITE_ENABLE_REAL_TIME !== 'false',
} as const;

// ============================================
// App Config
// ============================================

export const APP_CONFIG = {
  name: (import.meta.env.VITE_APP_NAME as string) || 'OB WorkFlow',
  version: (import.meta.env.VITE_APP_VERSION as string) || '1.0.0',
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const;

// ============================================
// API Config
// ============================================

export const API_CONFIG = {
  // Pagination defaults
  defaultPageSize: 20,
  maxPageSize: 100,
  
  // Retry config
  maxRetries: 3,
  retryDelay: 1000,
  
  // Cache TTL (in milliseconds)
  cacheTTL: 5 * 60 * 1000, // 5 minutes
} as const;

// ============================================
// Storage Keys
// ============================================

export const STORAGE_KEYS = {
  theme: 'ob-workflow-theme',
  sidebarCollapsed: 'ob-workflow-sidebar-collapsed',
  lastProject: 'ob-workflow-last-project',
  pushSubscribed: 'ob-workflow-push-subscribed',
} as const;

// ============================================
// Type exports
// ============================================

export type SupabaseConfig = typeof SUPABASE_CONFIG;
export type OneSignalConfig = typeof ONESIGNAL_CONFIG;
export type Features = typeof FEATURES;
export type AppConfig = typeof APP_CONFIG;
export type ApiConfig = typeof API_CONFIG;
